'use client'

import { cn } from '@brew/ui/cn'
import { useReducedMotion } from 'motion/react'
import { type MouseEvent, useRef, useState } from 'react'
import { AwardBadgeIcon } from '@/components/marketing/icons/award-badge-icon'

type AwardBadgeLinkProps = {
  link?: string
  className?: string
  /** The badge's accessible name, in the reader's language. */
  ariaLabel: string
  /** The award line on the badge ("Product of the Week #1"). */
  label: string
  eyebrowText: string
  eyebrowFontSize: number
  fillColor: string
}

/**
 * The Product Hunt badge as a link, with its hover choreography: the colour
 * overlays follow the cursor in, ease back out, and hold still under reduced
 * motion. `AwardBadge` (`award-badge.tsx`) gives it its words on the server.
 */
export function AwardBadgeLink({
  link,
  className,
  ariaLabel,
  label,
  eyebrowText,
  eyebrowFontSize,
  fillColor,
}: AwardBadgeLinkProps) {
  const ref = useRef<HTMLAnchorElement>(null)
  const shouldReduceMotion = useReducedMotion()
  const [firstOverlayPosition, setFirstOverlayPosition] = useState<number>(0)
  const [isInOutOverlayAnimationDisabled, setIsInOutOverlayAnimationDisabled] =
    useState<boolean>(true)
  const [isOverlayAnimationDisabled, setIsOverlayAnimationDisabled] =
    useState<boolean>(false)
  const enterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout1 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout2 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimeout3 = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getDimensions = () => {
    const rect = ref.current?.getBoundingClientRect()
    return {
      left: rect?.left ?? 0,
      right: rect?.right ?? 0,
      top: rect?.top ?? 0,
      bottom: rect?.bottom ?? 0,
    }
  }

  const computeCursorOffset = (clientX: number, clientY: number) => {
    const { left, right, top, bottom } = getDimensions()
    const xCenter = (left + right) / 2
    const yCenter = (top + bottom) / 2
    return (Math.abs(xCenter - clientX) + Math.abs(yCenter - clientY)) / 6
  }

  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    if (leaveTimeout1.current) {
      clearTimeout(leaveTimeout1.current)
    }
    if (leaveTimeout2.current) {
      clearTimeout(leaveTimeout2.current)
    }
    if (leaveTimeout3.current) {
      clearTimeout(leaveTimeout3.current)
    }
    setIsOverlayAnimationDisabled(true)

    setIsInOutOverlayAnimationDisabled(false)
    enterTimeout.current = setTimeout(
      () => setIsInOutOverlayAnimationDisabled(true),
      350
    )
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setFirstOverlayPosition(computeCursorOffset(e.clientX, e.clientY))
      })
    })
  }

  const handleMouseMove = (e: MouseEvent<HTMLAnchorElement>) => {
    setTimeout(
      () => setFirstOverlayPosition(computeCursorOffset(e.clientX, e.clientY)),
      150
    )
  }

  const handleMouseLeave = () => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current)
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsInOutOverlayAnimationDisabled(false)
        leaveTimeout1.current = setTimeout(
          () => setFirstOverlayPosition(-firstOverlayPosition / 4),
          150
        )
        leaveTimeout2.current = setTimeout(
          () => setFirstOverlayPosition(0),
          300
        )
        leaveTimeout3.current = setTimeout(() => {
          setIsOverlayAnimationDisabled(false)
          setIsInOutOverlayAnimationDisabled(true)
        }, 500)
      })
    })
  }

  return (
    <a
      ref={ref}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('block h-auto w-[180px] sm:w-[260px]', className)}
      aria-label={ariaLabel}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <AwardBadgeIcon
        fillColor={fillColor}
        eyebrowText={eyebrowText}
        eyebrowFontSize={eyebrowFontSize}
        label={label}
        overlayPosition={firstOverlayPosition}
        isOverlayTransitionEnabled={
          !(isInOutOverlayAnimationDisabled || shouldReduceMotion)
        }
        isOverlayAnimationEnabled={
          !(isOverlayAnimationDisabled || shouldReduceMotion)
        }
      />
    </a>
  )
}
